package org.multiplatform.kotfolio.pages

import androidx.compose.runtime.*
import org.multiplatform.kotfolio.components.AppearanceAwareImage
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.util.Resources
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ul
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties

@Page("api_calling_rpc")
@Composable
fun APICallingRPCPage() {

    BlogLayout(
        PageProperties(
            "RPC",
            ""
        ),
        BlogProperties("Jetpack Compose", "RPC")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            Ul {
                Li {
                    SpanText(
                        text = "Kotlin RPC (Remote Procedure Call) is a new and exciting addition to the Kotlin ecosystem, which builds on well-established foundations and runs on the kotlinx.rpc library.",
                        modifier = Modifier.padding(topBottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }

                Li {
                    SpanText(
                        text = "The kotlinx.rpc library enables you to make procedure calls over network boundaries using nothing but regular Kotlin language constructs. As such, it provides an alternative to both REST and Google RPC (gPRC).",
                        modifier = Modifier.padding(topBottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }

                Li {
                    SpanText(
                        text = "kotlinx.rpc is a Kotlin library for adding asynchronous Remote Procedure Call (RPC) services to your applications.",
                        modifier = Modifier.padding(topBottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }

                Li {
                    SpanText(
                        text = "Local procedure calls, since they always take place within the same program. A remote procedure call is when the function call and parameters are in some way transferred over a network, so that the implementation can occur within a separate VM/executable. The return value travels the opposite path back to the machine where the invocation was made.",
                        modifier = Modifier.padding(topBottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }
            }

            KotlinCode(
                code = """
                    1. Post Method Choose
                    2. Body parameter

                    {
                        "type": "Grid",
                         "items": [
                        {
                          "id": 1,
                          "name": "Gokul",
                          "description": "El protagonista de la serie, conocido por su gran poder y personalidad amigable. Originalmente enviado a la Tierra como un infante volador con la misión de conquistarla. Sin embargo, el caer por un barranco le proporcionó un brutal golpe que si bien casi lo mata, este alteró su memoria y anuló todos los instintos violentos de su especie, lo que lo hizo crecer con un corazón puro y bondadoso, pero conservando todos los poderes de su raza. No obstante, en la nueva continuidad de Dragon Ball se establece que él fue enviado por sus padres a la Tierra con el objetivo de sobrevivir a toda costa a la destrucción de su planeta por parte de Freeza. Más tarde, Kakarot, ahora conocido como Son Goku, se convertiría en el príncipe consorte del monte Fry-pan y líder de los Guerreros Z, así como el mayor defensor de la Tierra y del Universo 7, logrando mantenerlos a salvo de la destrucción en innumerables ocasiones, a pesar de no considerarse a sí mismo como un héroe o salvador.",
                          "image": "https://dragonball-api.com/characters/goku_normal.webp"
                        }
                      ]
                    }
                    """.trimIndent()
            )

            Box(
                modifier = Modifier.fillMaxSize().padding(topBottom = 1.cssRem),
                contentAlignment = Alignment.Center
            ) {
                Row(
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Link(
                        path = "https://github.com/santimattius/kmp-rpc-example",
                        text = "KMP - RPC",
                        modifier = Modifier
                            .margin(right = 4.px)
                    )
                    AppearanceAwareImage(
                        src = Resources.Images.NAVIGATION_ARROW
                    )
                }
            }

            KotlinCode(
                code = """
                    import io.ktor.client.*
                    import io.ktor.client.plugins.contentnegotiation.*
                    import io.ktor.client.plugins.resources.*
                    import io.ktor.client.plugins.websocket.*
                    import io.ktor.serialization.kotlinx.*
                    import io.ktor.serialization.kotlinx.cbor.*
                    import kotlinx.rpc.krpc.ktor.client.installKrpc
                    import kotlinx.rpc.krpc.serialization.cbor.cbor
                    import kotlinx.serialization.ExperimentalSerializationApi
                    import io.ktor.client.HttpClient as KtorHttpClient

                    @OptIn(ExperimentalSerializationApi::class)
                    fun HttpClient(
                        configure: HttpClientConfig<*>.() -> Unit = {},
                    ): KtorHttpClient = KtorHttpClient {
                        installKrpc {
                            serialization {
                                cbor(DefaultCbor)
                            }
                        }
                        install(ContentNegotiation) {
                            cbor(DefaultCbor)
                        }
                        install(WebSockets) {
                            contentConverter = KotlinxWebsocketSerializationConverter(DefaultCbor)
                        }
                        install(Resources)
                        followRedirects = true
                        expectSuccess = true

                        configure()
                    }
                    
                    import io.ktor.client.*
                    import io.ktor.client.request.*
                    import io.ktor.http.*
                    import kotlinx.coroutines.*
                    import kotlinx.coroutines.channels.Channel
                    import kotlinx.coroutines.channels.Channel.Factory.UNLIMITED
                    import kotlinx.coroutines.sync.Mutex
                    import kotlinx.coroutines.sync.withLock
                    import kotlinx.rpc.annotations.Rpc
                    import kotlinx.rpc.krpc.ktor.client.KtorRpcClient
                    import kotlinx.rpc.krpc.ktor.client.rpc
                    import kotlinx.rpc.withService
                    import kotlin.time.Duration.Companion.seconds

                    interface DurableRpcService<@Rpc T : Any> {

                        fun durableCall(f: suspend T.() -> Unit)

                        fun durableLaunch(f: suspend T.() -> Unit)
                    }

                    @OptIn(DelicateCoroutinesApi::class)
                    inline fun <@Rpc reified T : Any> DurableRpcService(
                        coroutineScope: CoroutineScope,
                        httpClient: HttpClient,
                        path: String,
                        crossinline configureRequest: HttpRequestBuilder.() -> Unit,
                    ): DurableRpcService<T> = object : DurableRpcService<T> {
                        private var client: KtorRpcClient? = null
                        private val mutex = Mutex()

                        private inline val KtorRpcClient.hasActiveConnection: Boolean
                            get() = webSocketSession.isActive && !webSocketSession.outgoing.isClosedForSend

                        private val lastActiveClient: KtorRpcClient?
                            get() = client?.takeIf { it.hasActiveConnection }

                        private val callQueue = Channel<suspend T.() -> Unit>(UNLIMITED)
                        private val launchQueue = Channel<suspend T.() -> Unit>(UNLIMITED)

                        private suspend fun ensureActiveClient(): KtorRpcClient = lastActiveClient ?: mutex.withLock {
                            lastActiveClient?.let { return@withLock it }
                            httpClient.rpc(path) {
                                method = HttpMethod.Get
                                configureRequest()
                            }.also {
                                client = it
                            }
                        }

                        private suspend inline fun retry(crossinline f: suspend () -> Unit) {
                            var duration = 0.25.seconds
                            loop@ while (currentCoroutineContext().isActive) runCatching {
                                f()
                                break@loop
                            }.onFailure {
                                delay(duration)
                                duration = minOf(4.seconds, duration * 2)
                            }
                        }

                        private suspend inline fun <U> cancellable(crossinline f: suspend T.() -> U): U {
                            return coroutineScope {
                                val client = ensureActiveClient()
                                val checkJob = launch {
                                    while (currentCoroutineContext().isActive) {
                                        delay(1.seconds)
                                        if (client.hasActiveConnection) continue
                                        this@coroutineScope.cancel("websocket connection closed")
                                    }
                                }
                                client.withService<T>().f().also {
                                    checkJob.cancelAndJoin()
                                }
                            }
                        }

                        init {
                            coroutineScope.launch {
                                for (call in callQueue) retry {
                                    cancellable(call)
                                }
                            }
                            coroutineScope.launch {
                                for (launch in launchQueue) coroutineScope.launch {
                                    retry {
                                        cancellable(launch)
                                    }
                                }
                            }
                        }

                        override fun durableCall(f: suspend T.() -> Unit) {
                            callQueue.trySend(f)
                        }

                        override fun durableLaunch(f: suspend T.() -> Unit) {
                            launchQueue.trySend(f)
                        }
                    }
                    """.trimIndent()
            )
        }
    }
}

//https://github.com/santimattius/kmp-rpc-example
//https://github.com/ishabodiwala/NewsBot-MCP
//https://github.com/ozgurdemirel/kprc-and-kmp-notebook-demo (Backend and Desktop)